import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Check, X, Shield } from "lucide-react";
import Logo from "../components/Logo";

// Head-to-head comparison stays hardcoded — it's a marketing narrative, not
// broker-configurable data, and gives the page instant paint (no waterfall).
const COMPARISON = [
  { feature: "Commission model", core: "Flat Fee Per Transaction", franchise: "60/40 → 80/20 split" },
  { feature: "Agent take-home on $9,000 GCI", core: "~$8,500 net", franchise: "$5,400 – $7,200 net" },
  { feature: "Desk fee", core: "$85", franchise: "$50 – $150/mo" },
  { feature: "Royalty / brand tax", core: "0%", franchise: "5 – 8%" },
  { feature: "Annual cap chase", core: "None — flat fee is the cap", franchise: "Monthly until cap is met" },
  { feature: "Direct broker access", core: "Same week, same broker", franchise: "Team lead → manager → maybe" },
  { feature: "Contract + compliance review", core: "Included on every deal", franchise: "Often paywalled or queued" },
  { feature: "Brand freedom", core: "Build your own under CORE Realty and Associates umbrella", franchise: "Strict franchise lockup" },
  { feature: "Mentorship for new agents", core: "Included", franchise: "Add-on / team-only" },
  { feature: "GAR Forms, CANVA Marketing Templates Included", core: true, franchise: false },
  { feature: "Local lead routing", core: true, franchise: false },
  { feature: "Team-build rights", core: true, franchise: "Often restricted" },
];

export default function Tiers() {
  // Splits section (formerly on /recruit) — broker-managed via /api/public/recruit.
  const [tiers, setTiers] = useState(null);
  useEffect(() => {
    api.get("/public/recruit").then((r) => setTiers(r.data?.tiers || [])).catch(() => setTiers([]));
  }, []);

  return (
    <div className="space-y-10" data-testid="tiers-page">
      <header>
        <div className="overline">Brokerage · Fees &amp; Commission Splits</div>
        <h1 className="font-heading text-5xl mt-2 leading-[1.05]">Every fee. Every split. On one page.</h1>
        <p className="text-sm text-[#050303]/70 mt-3 max-w-2xl">
          CORE Realty and Associates LLC is a boutique Metro Atlanta brokerage with one promise: keep your commission,
          keep your brand, keep the broker on speed-dial. Here&apos;s exactly how our flat-fee model works — and how it
          stacks up against a big-box franchise split.
        </p>
        <a href="/recruit#apply" className="mt-6 inline-flex items-center gap-2 bg-[#050303] text-white pill-btn hover:bg-[#bf858e]" data-testid="tiers-apply-cta">
          Apply to CORE Realty and Associates
        </a>
      </header>

      <section data-testid="splits-section">
        <div className="overline">Flat Fees &amp; FMLS Fees</div>
        <h2 className="font-heading text-4xl mt-2 mb-6">Choose the plan that fits how you close.</h2>
        {tiers === null ? (
          <div className="core-card p-7 text-sm text-[#050303]/60">Loading fee schedule…</div>
        ) : tiers.length === 0 ? (
          <div className="core-card p-7 text-sm text-[#050303]/60">Fee schedule coming soon — ask the broker.</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {tiers.map((t) => (
              <div key={t.id} className="core-card p-7" data-testid={`tier-card-${t.id}`}>
                <div className="overline">{t.name}</div>
                <div className="font-heading text-4xl mt-3 text-[#bf858e] break-words">{t.split}</div>
                {t.requirement && <div className="text-xs text-[#050303]/60 mt-3">{t.requirement}</div>}
                {(t.perks || []).length > 0 && (
                  <ul className="mt-5 space-y-2 text-sm">
                    {(t.perks || []).map((p) => (
                      <li key={p} className="flex gap-2">
                        <Check size={14} className="text-[#bf858e] mt-0.5 shrink-0" />{p}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
          </div>
        )}
      </section>

      <section className="core-card p-7" data-testid="franchise-comparison">
        <div className="overline flex items-center gap-2"><Shield size={13} strokeWidth={1.5} /> Head-to-Head</div>
        <h3 className="font-heading text-3xl mt-2 mb-5">CORE Realty and Associates flat-fee vs. franchise split brokerages</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="border-b border-[#d1cece]">
              <tr className="text-left">
                <th className="py-3 font-semibold">Feature</th>
                <th className="py-3 font-semibold text-[#bf858e]">CORE Realty and Associates (Flat Fee Per Transaction)</th>
                <th className="py-3 font-semibold text-[#050303]/60">Big-box Franchise (60/40 → 80/20)</th>
              </tr>
            </thead>
            <tbody>
              {COMPARISON.map((c) => (
                <tr key={c.feature} className="border-b border-[#d1cece] last:border-0">
                  <td className="py-3 pr-4">{c.feature}</td>
                  <td className="py-3 pr-4 font-semibold">{renderCell(c.core)}</td>
                  <td className="py-3 text-[#050303]/70">{renderCell(c.franchise)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="core-card p-8 bg-[#050303] text-white" data-testid="closing-cta">
        <div className="flex items-center gap-4 mb-6">
          <Logo variant="white" height={48} />
          <span
            className="text-[10px] font-semibold tracking-[0.24em] uppercase text-[#e1b7c1]"
            data-testid="brand-motto"
          >
            Real Estate Integrity at its CORE
          </span>
        </div>
        <div className="overline text-white/60">Ready to keep what you earn?</div>
        <h3 className="font-heading text-4xl mt-2 max-w-2xl">Bring your book to a brokerage that doesn&apos;t take a percentage of your success.</h3>
        <a href="/recruit#apply" className="mt-6 inline-flex items-center gap-2 bg-[#e1b7c1] text-[#050303] pill-btn hover:bg-white" data-testid="tiers-apply-cta-bottom">
          Apply to CORE Realty and Associates
        </a>
      </section>
    </div>
  );
}

function renderCell(v) {
  if (v === true) return <Check size={16} strokeWidth={2} className="text-[#bf858e]" />;
  if (v === false) return <X size={16} strokeWidth={2} className="text-[#050303]/30" />;
  return v;
}
